/**
 * Dependencies API - Check asset dependencies before deletion.
 */

import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import type { DependencyCheckResult, AssetType, DependencyGroup } from '../../components/DependencyWarningModal'

export interface DependencyCheckParams {
  page?: number
  page_size?: number
  search?: string
  filter_type?: string
  signal?: AbortSignal
  tenantId?: string
}

export interface DependencyCountResult {
  count: number
  can_delete: boolean
}

export const dependenciesApi = {
  /**
   * Check dependencies for an asset.
   * Returns detailed dependency information for UI display.
   */
  check: (
    assetType: AssetType,
    assetId: string,
    params?: DependencyCheckParams
  ): Promise<DependencyCheckResult> => {
    const { signal, tenantId, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<DependencyCheckResult>(`/dependencies/${assetType}/${assetId}${query}`, {
      signal,
      tenantId,
    })
  },

  /**
   * Quick count of dependencies without full details.
   * Use for checking if delete button should show warning.
   */
  count: (
    assetType: AssetType,
    assetId: string,
    options?: { signal?: AbortSignal }
  ): Promise<DependencyCountResult> => {
    return request<DependencyCountResult>(`/dependencies/${assetType}/${assetId}/count`, {
      signal: options?.signal,
    })
  },

  /**
   * Bulk check dependencies for multiple assets of the same type.
   * Returns a merged DependencyCheckResult for showing one modal before bulk delete.
   */
  bulkCheck: (
    assetType: AssetType,
    assetIds: string[],
    options?: { signal?: AbortSignal; tenantId?: string }
  ): Promise<DependencyCheckResult> => {
    return request<DependencyCheckResult>('/dependencies/bulk-check', {
      method: 'POST',
      body: JSON.stringify({ asset_type: assetType, asset_ids: assetIds }),
      signal: options?.signal,
      tenantId: options?.tenantId,
    })
  },
}

export default dependenciesApi

function mergeDependencyResults(results: DependencyCheckResult[]): DependencyCheckResult {
  if (results.length === 1) return results[0]
  const byType: Record<string, DependencyGroup> = {}
  let total = 0
  let canDelete = true
  const warnings: string[] = []
  for (const result of results) {
    canDelete = canDelete && result.can_delete
    total += result.total_count
    if (result.warning_message) warnings.push(result.warning_message)
    for (const [type, group] of Object.entries(result.dependencies_by_type || {})) {
      const existing = byType[type]
      if (!existing) {
        byType[type] = { ...group, items: [...group.items] }
      } else {
        existing.count += group.count
        existing.items.push(...group.items)
        existing.has_more = existing.has_more || group.has_more
      }
    }
  }
  return {
    can_delete: canDelete,
    total_count: total,
    warning_message: warnings.join(' '),
    dependencies_by_type: byType,
    page: 1,
    page_size: Math.max(total, 1),
    total_pages: 1,
  }
}

/** Run bulk dependency checks once per tenant so All-mode deletes stay in the asset's tenant. */
export async function bulkCheckByTenant(
  assetType: AssetType,
  items: Array<{ id: string; tenantId?: string }>
): Promise<DependencyCheckResult> {
  const groups = new Map<string, string[]>()
  for (const item of items) {
    const key = item.tenantId ?? ''
    const bucket = groups.get(key) ?? []
    bucket.push(item.id)
    groups.set(key, bucket)
  }
  const results: DependencyCheckResult[] = []
  for (const [key, ids] of groups) {
    results.push(
      await dependenciesApi.bulkCheck(assetType, ids, { tenantId: key || undefined })
    )
  }
  if (results.length === 0) {
    return {
      can_delete: true,
      total_count: 0,
      warning_message: '',
      dependencies_by_type: {},
      page: 1,
      page_size: 1,
      total_pages: 1,
    }
  }
  return mergeDependencyResults(results)
}
